<template>
  <canvas
    ref="canvasRef"
    aria-hidden="true"
    class="text-onaction-default-hi font-body-1 pointer-events-none absolute z-0 h-[calc(100%+16px)] w-[calc(100%+16px)]"
    :style="snowStyle"
    data-test="shopping-assistant-festive-snow"
  />
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import {
  DEFAULT_SNOWFALL_OPTIONS,
  SnowfallEngine,
} from './snowfall/snowfallEngine'

const props = withDefaults(
  defineProps<{
    // The host element's effective corner radius, in px, so the snow
    // canvas — overscanned 8px per side — is clipped back to the host's
    // own rounded shape. The FAB is `rounded-lg` (Figma radius 12); the PLP
    // gifting pill is `rounded-[9999px]` × `h-36` (effective radius 18).
    clipRadiusPx?: number
    // Whether the host is currently visible (v-show). When false the rAF
    // loop is paused — no main-thread wakeups for an invisible layer. The
    // engine's own ResizeObserver re-syncs size and flakes once visible
    // again. Defaults to always-visible (e.g. the PLP pill, which is only
    // rendered when shown and has no show/hide store).
    visible?: boolean
  }>(),
  {
    clipRadiusPx: 12,
    visible: true,
  },
)

// The snow layer extends 8px beyond the host on every side and clips
// there (1:1 overscan), so the radius passed is the host's own.
const SNOW_OVERSIZED_PX = 8

// Slow the snowfall down while the host is hovered, per the Figma dev
// annotation ("slow down .5x on hover").
const HOVER_SLOWDOWN = 0.5

const snowStyle = computed(() => ({
  top: `${-SNOW_OVERSIZED_PX}px`,
  left: `${-SNOW_OVERSIZED_PX}px`,
  // Canvas is a replaced element: insets alone don't size it, so its
  // width/height classes do (calc(100% + 16px) via the template). Clip the
  // snow to the host's own rounded shape: the canvas box is 8px overscanned
  // per side, so inset(8px) restores the host box and `round <radius>` matches
  // its corner radius.
  clipPath: `inset(${SNOW_OVERSIZED_PX}px round ${props.clipRadiusPx}px)`,
}))

const canvasRef = ref<HTMLCanvasElement | null>(null)

let engine: SnowfallEngine | null = null

// Hover targets the host element (the canvas is pointer-events-none), so
// the hover listeners live on the canvas's parent element.
let host: HTMLElement | null = null
const onEnter = () => engine?.setHovered(true)
const onLeave = () => engine?.setHovered(false)

function resolveTokens() {
  // The canvas element itself carries the design-token probe classes
  // (`text-onaction-default-hi` for the glyph color, `font-body-1` for the
  // BM Duplet font family): reading its computed style resolves the
  // mood-aware CSS custom properties into concrete values for the 2D
  // context, which can't consume `var(...)` directly. A canvas ignores
  // text styling itself, so the probe classes have no visual side effect.
  const computedStyle = canvasRef.value
    ? window.getComputedStyle(canvasRef.value)
    : null

  return {
    color: computedStyle?.color || DEFAULT_SNOWFALL_OPTIONS.color,
    fontFamily:
      computedStyle?.fontFamily || DEFAULT_SNOWFALL_OPTIONS.fontFamily,
  }
}

// Pause the rAF loop while the host is hidden (v-show) — no main-thread
// wakeups for an invisible layer. The engine's own ResizeObserver re-syncs
// size and flakes once visible again.
watch(
  () => props.visible,
  (visible) => {
    if (!engine || engine.isReducedMotion()) return

    if (visible) {
      engine.start()
    } else {
      engine.stop()
    }
  },
)

onMounted(() => {
  const canvas = canvasRef.value
  if (!canvas) return

  const { color, fontFamily } = resolveTokens()

  engine = new SnowfallEngine(canvas, {
    ...DEFAULT_SNOWFALL_OPTIONS,
    color,
    fontFamily,
    // Half speed while hovered, per the Figma dev annotation.
    hoverSlowdown: HOVER_SLOWDOWN,
  })

  host = canvas.parentElement
  host?.addEventListener('pointerenter', onEnter)
  host?.addEventListener('pointerleave', onLeave)

  if (props.visible) {
    engine.start()
  }
})

onBeforeUnmount(() => {
  host?.removeEventListener('pointerenter', onEnter)
  host?.removeEventListener('pointerleave', onLeave)

  engine?.destroy()
  engine = null
})
</script>
